/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, {
    FunctionComponent,
    useEffect,
    useLayoutEffect,
    useReducer,
    useRef,
    useState,
} from 'react';

import { MINUTE_TO_PIXEL_FACTOR, PROGRAM_CELL_TRAILING_ICONS_WIDTH_OFFSET } from '../../../config';
import { DimensionScaler } from '../../../core/device/DimensionScaler';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { GuideType } from '../../../core/enums';
import { ServicesClientImpl } from '../../../core/interactors';
import { RecordingRestriction } from '../../../core/usecases';
import { CellStatusScheduler } from '../../../core/utils';
import { Mousetrap, RestrictionMessage } from '../../common';
import { FilteredGuideHighlight } from '../FilteredGuide/FilteredGuideHighlight/FilteredGuideHighlight';
import { GuideHighlight } from '../GuideHighlight';
import { RecordingStatusIcon } from '../RecordingStatusIcon';
import { CellTypes } from './CellTypes';
import { ProgramCellProps } from './ProgramCellProps';

import './ProgramCell.scss';

let nextCellId = 0;
const forceRenderReducer = (count: number) => count + 1;

const ProgramCell: FunctionComponent<ProgramCellProps> = (props): JSX.Element => {
    const {
        channel,
        duration,
        endTime,
        highlightId,
        isCatchup,
        isLeft,
        isNew,
        isPastGuideMode,
        isRight,
        isStartover,
        isSubscribed,
        movieYear,
        programCell,
        startTime,
        title,
        type,
        cuAvailabilityDuration,
        isRefreshed,
        recordingStatus,
        guideType,
    } = props;

    const [isSocu, setIsSocu] = useState<boolean>(() => {
        const now = Date.now();
        if (isStartover && startTime && endTime) {
            if (startTime.getTime() <= now && now < endTime.getTime()) return true;
        }
        if (isCatchup && endTime && startTime && cuAvailabilityDuration) {
            if (endTime.getTime() <= now && now - endTime.getTime() <= cuAvailabilityDuration) {
                return true;
            }
        }
        return false;
    });
    const [showNewIcon, setShowNewIcon] = useState<boolean>(!!isNew);
    const cellIdRef = useRef<string | null>(null);
    if (cellIdRef.current === null) {
        cellIdRef.current = `cell-${nextCellId}`;
        nextCellId += 1;
    }
    const schedulerIdsRef = useRef<string[]>([]);
    const socuIconSpanRef = useRef<HTMLImageElement>(null);
    const programCellContainerRef = useRef<HTMLDivElement>(null);
    const programCellRef = useRef<HTMLDivElement>(null);
    const programCellSpanTextRef = useRef<HTMLSpanElement>(null);
    const decorativeIconSpanRef = useRef<HTMLSpanElement>(null);
    const [, forceRender] = useReducer(forceRenderReducer, 0);

    const cellType = type || CellTypes.DEFAULT;
    const programCellTitle = title || '';
    const displayText = movieYear ? `${programCellTitle} (${movieYear})` : programCellTitle;
    const from = Math.max(0, displayText.length - 4);
    const trailingText = displayText.slice(from, from + 4) || '';
    let containerWidth = (duration || 0) * MINUTE_TO_PIXEL_FACTOR - 3;
    const isSafeLeft = containerWidth > 20 && isLeft;
    const isSafeRight = containerWidth > 20 && isRight;
    const customStyle: any = {};
    const services = new ServicesClientImpl();
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);
    const socuIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.SOURCE_SOCU_ICON);

    const isNonTrailingOrLeading = !(
        cellType === CellTypes.TRAILING || cellType === CellTypes.LEADING
    );

    if (cellType === CellTypes.FUTURE_CONTINUOUS && !isPastGuideMode) {
        containerWidth += 6;
        customStyle.marginRight = 0;
    }
    customStyle.width = DimensionScaler.getInstance().getScaledWidth(containerWidth, false);

    const programCellContainerWidth = programCellContainerRef.current?.clientWidth || 0;
    const decorativeIconSpanWidth = decorativeIconSpanRef.current?.scrollWidth || 0;
    const programCellWidth = programCellRef.current?.clientWidth || 0;
    const hasOverflow =
        (programCellSpanTextRef.current?.scrollWidth || 0) >
        programCellWidth - decorativeIconSpanWidth;
    const programCellSpanTextClasses = `text${isSubscribed ? '' : ' cell-fadeout'}${hasOverflow ? ' cell-fade-effect' : ''}`;

    // here 24 is margin from left and right side of the cell.
    let programCellSpanTextWidth = programCellWidth - 24 - decorativeIconSpanWidth;
    programCellSpanTextWidth = Math.min(programCellSpanTextWidth, programCellContainerWidth);
    const decorativeIconPosition =
        containerWidth - (programCellSpanTextWidth + decorativeIconSpanWidth) - 24;

    /* creating the classname from recordingStatus string by replacing uppercase
       letter with -matched letter in lowercase.
       e.g. scheduledAsOnePass -> scheduled-as-one-pass */
    const recordingStatusClass = recordingStatus?.replace(
        /[A-Z]/g,
        (match) => `-${match.toLowerCase()}`,
    );

    const scheduleStatusChange = (suffix: string, enableTime: number, callback: () => void) => {
        const id = `${cellIdRef.current}-${suffix}`;
        CellStatusScheduler.register(id, enableTime, callback);
        schedulerIdsRef.current.push(id);
    };

    const disableCatchupAfterCatchupDuration = () => {
        if (cuAvailabilityDuration && startTime) {
            const enableTime = startTime.getTime() + cuAvailabilityDuration;
            scheduleStatusChange('cu-disable', enableTime, () => {
                setIsSocu(false);
            });
        }
    };

    const validateAndSetCatchupStatus = () => {
        if (
            cuAvailabilityDuration &&
            endTime &&
            Date.now() - endTime.getTime() <= cuAvailabilityDuration
        ) {
            setIsSocu(true);
            disableCatchupAfterCatchupDuration();
        } else {
            setIsSocu(false);
        }
    };

    const checkStartoverAndSetStatus = () => {
        if (isStartover && startTime && endTime) {
            const currentTime = Date.now();
            const isCurrentlyAiring =
                startTime.getTime() <= currentTime && currentTime < endTime.getTime();
            const willAirInFuture = currentTime < startTime.getTime();
            if (isCurrentlyAiring) setIsSocu(true);
            else if (willAirInFuture) {
                scheduleStatusChange('so-enable', startTime.getTime(), () => {
                    setIsSocu(true);
                });
            }
        }
    };

    const checkCatchupAndSetStatus = () => {
        if (isCatchup && endTime && startTime) {
            const currentTime = Date.now();
            const isPastProgram = endTime.getTime() <= currentTime;
            const willAirInFuture = currentTime < startTime.getTime();
            if (isPastProgram) {
                validateAndSetCatchupStatus();
            } else if (willAirInFuture) {
                scheduleStatusChange('cu-enable', endTime.getTime(), () => {
                    validateAndSetCatchupStatus();
                });
            }
        }
    };

    const clearScheduledEntries = () => {
        schedulerIdsRef.current.forEach((id) => CellStatusScheduler.unregister(id));
        schedulerIdsRef.current = [];
    };

    useLayoutEffect(() => {
        forceRender();
    }, []);

    useEffect(() => {
        checkStartoverAndSetStatus();
        checkCatchupAndSetStatus();
        return () => {
            clearScheduledEntries();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isRefreshed) {
            clearScheduledEntries();
            checkStartoverAndSetStatus();
            checkCatchupAndSetStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRefreshed]);

    useEffect(() => {
        if (isSocu)
            setShowNewIcon((showIcon) => {
                return (
                    showIcon &&
                    decorativeIconSpanWidth + PROGRAM_CELL_TRAILING_ICONS_WIDTH_OFFSET <
                        (programCellRef.current?.offsetWidth || 0)
                );
            });
        else {
            setShowNewIcon(!!isNew);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSocu, decorativeIconSpanWidth]);

    return (
        <React.Fragment>
            {isNonTrailingOrLeading ? (
                <Mousetrap
                    className="program-cell-container"
                    value={highlightId || ''}
                    style={customStyle}
                    ref={programCellContainerRef}
                >
                    {guideType === GuideType.FILTERED_GUIDE ? (
                        <FilteredGuideHighlight id={highlightId} cellType={cellType} />
                    ) : (
                        <GuideHighlight id={highlightId} cellType={cellType} />
                    )}

                    <RestrictionMessage
                        displayType={RecordingRestriction.PROGRAM_CELL}
                        channel={channel}
                        programCell={programCell}
                    />
                    <div ref={programCellRef} className="program-cell">
                        {isSafeLeft ? <span className="left-pointer"></span> : null}
                        <span
                            ref={programCellSpanTextRef}
                            className={programCellSpanTextClasses}
                            style={{
                                width: hasOverflow
                                    ? `${((programCellSpanTextWidth / (programCellContainerRef.current?.offsetWidth || 1)) * 100).toFixed(2)}%`
                                    : 'auto',
                            }}
                        >
                            {displayText}
                        </span>
                        <span
                            className="decorative-icons"
                            ref={decorativeIconSpanRef}
                            style={{
                                right: `${((decorativeIconPosition / (programCellContainerRef.current?.offsetWidth || 1)) * 100).toFixed(2)}`,
                            }}
                        >
                            {showNewIcon ? (
                                <img
                                    src={newIcon?.imageUrl}
                                    className="icon-new"
                                    data-testid={'program-cell-new-icon'}
                                />
                            ) : null}
                            {isSocu ? (
                                <img
                                    src={socuIcon?.imageUrl}
                                    className="icon-socu"
                                    ref={socuIconSpanRef}
                                />
                            ) : null}
                            {recordingStatus ? (
                                <RecordingStatusIcon
                                    type={recordingStatus}
                                    styleClass={recordingStatusClass}
                                />
                            ) : null}
                        </span>
                        {isSafeRight ? <span className="right-pointer"></span> : null}
                    </div>
                </Mousetrap>
            ) : null}

            {cellType === CellTypes.TRAILING ? (
                <div className="program-cell-trailing-container">
                    <div className="text">{trailingText}</div>
                </div>
            ) : null}

            {cellType === CellTypes.LEADING ? (
                <div className="program-cell-leading-container">
                    <div className="text">{displayText}</div>
                </div>
            ) : null}
        </React.Fragment>
    );
};

export default React.memo(ProgramCell);
